<template>
  <div class="quote-print-stage" aria-hidden="true">
    <div class="quote-print-measure">
      <div ref="notesMeasure" class="notes-line notes-measure" aria-hidden="true"></div>
      <table class="details-table">
        <colgroup>
          <col class="item-col" />
          <col class="description-col" />
          <col class="qty-col" />
          <col class="price-col" />
          <col class="amount-col" />
        </colgroup>
        <tbody>
          <tr v-for="line in printableLines" :key="`measure-${line.printKey}`" ref="measureRows" :class="rowClass(line)">
            <td class="item-number">{{ line.printNumber }}</td>
            <td class="description-cell">
              <template v-if="line.isComment">
                <div class="comment-text">{{ line.DESCRIPTION }}</div>
              </template>
              <template v-else>
                <div v-if="descriptionParts(line).code" class="material-code">{{ descriptionParts(line).code }}</div>
                <div v-for="(part, index) in descriptionParts(line).details" :key="index">{{ part }}</div>
              </template>
            </td>
            <td class="numeric-cell quantity-cell">
              <template v-if="!line.isComment">
                <div>{{ formatQuantity(line) }} {{ unitAbbreviation(line.UNIT) }}</div>
              </template>
            </td>
            <td class="numeric-cell price-cell">
              <template v-if="!line.isComment">
                <div>{{ money(line.UNITPRICE) }}</div>
                <div v-if="lineDiscountPct(line)" class="line-discount">Discount {{ formatPercent(lineDiscountPct(line)) }}%</div>
              </template>
            </td>
            <td class="numeric-cell amount-cell">
              <template v-if="!line.isComment">{{ money(line.amount) }}</template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <section v-for="(page, pageIndex) in pages" :key="page.key" class="quote-print-page">
      <header class="print-header">
        <div class="logo-block">
          <img :src="logoPrint" alt="Western Interlock" />
        </div>
        <div class="letterhead">
          <div>10095 Rickreall Rd</div>
          <div>Rickreall, OR</div>
          <div>97371-9344</div>
          <div><strong>Phone</strong> <span>(503) 623-9084</span></div>
          <div><strong>E-Mail</strong> <span>sales@westerninterlock.com</span></div>
        </div>
        <div class="quotation-title">
          <div>Quotation</div>
          <span>{{ quoteNumber }}</span>
        </div>
      </header>

      <div class="print-body">
        <div v-if="page.isFirst" class="first-page-info">
          <section class="info-panel customer-panel">
            <h2>Customer Address</h2>
            <div class="panel-body">
              <div v-for="(line, index) in customerAddressLines" :key="`customer-${index}`">{{ line }}</div>
              <div v-if="customerPhone" class="phone-line"><strong>Phone:</strong> {{ customerPhone }}</div>
            </div>
          </section>

          <section class="info-panel shipping-panel">
            <h2>Shipping Address</h2>
            <div class="panel-body">
              <div v-for="(line, index) in shippingAddressLines" :key="`shipping-${index}`">{{ line }}</div>
              <div v-if="quote.SHIPPHONE" class="phone-line"><strong>Phone:</strong> {{ quote.SHIPPHONE }}</div>
            </div>
          </section>

          <section class="info-panel branch-panel">
            <h2>Information</h2>
            <div ref="branchPanelBody" class="panel-body branch-panel-body">
              <div ref="branchFixedLines" class="branch-fixed-lines">
                <div>WESTERN INTERLOCK - RICKREALL</div>
                <div>10095 RICKREALL RD</div>
                <div>RICKREALL OR 97371-9344</div>
                <div class="phone-line"><strong>Phone:</strong> 503-783-3490</div>
                <div v-if="quote.VALIDTO" class="valid-to-line"><strong>Valid To:</strong> {{ formatDate(quote.VALIDTO) }}</div>
                <div class="total-weight-line"><strong>Total Weight:</strong> {{ formattedTotalWeight }}</div>
              </div>
              <div v-if="displayNotes" class="notes-line"><strong>Notes:</strong> {{ displayNotes }}</div>
            </div>
          </section>
        </div>

        <section class="details-panel" :style="{ height: `${page.detailsHeight}px` }">
          <h2>
            <span>Quotation Details</span>
            <span class="page-number">Page {{ pageIndex + 1 }} of {{ pages.length }}</span>
          </h2>
          <table class="details-table">
            <colgroup>
              <col class="item-col" />
              <col class="description-col" />
              <col class="qty-col" />
              <col class="price-col" />
              <col class="amount-col" />
            </colgroup>
            <thead>
              <tr>
                <th>Item</th>
                <th>Material / Description</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in page.rows" :key="line.printKey" :class="rowClass(line)">
                <td class="item-number">{{ line.printNumber }}</td>
                <td class="description-cell">
                  <template v-if="line.isComment">
                    <div class="comment-text">{{ line.DESCRIPTION }}</div>
                  </template>
                  <template v-else>
                    <div v-if="descriptionParts(line).code" class="material-code">{{ descriptionParts(line).code }}</div>
                    <div v-for="(part, index) in descriptionParts(line).details" :key="index">{{ part }}</div>
                  </template>
                </td>
                <td class="numeric-cell quantity-cell">
                  <template v-if="!line.isComment">
                    <div>{{ formatQuantity(line) }} {{ unitAbbreviation(line.UNIT) }}</div>
                  </template>
                </td>
                <td class="numeric-cell price-cell">
                  <template v-if="!line.isComment">
                    <div>{{ money(line.UNITPRICE) }}</div>
                    <div v-if="lineDiscountPct(line)" class="line-discount">Discount {{ formatPercent(lineDiscountPct(line)) }}%</div>
                  </template>
                </td>
                <td class="numeric-cell amount-cell">
                  <template v-if="!line.isComment">{{ money(line.amount) }}</template>
                </td>
              </tr>
              <tr v-if="page.fillerHeight > 0" class="filler-row" :style="{ height: `${page.fillerHeight}px` }">
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              <tr v-if="page.isLast" class="totals-row material-total-row">
                <td></td>
                <td></td>
                <td class="total-label">Subtotal</td>
                <td></td>
                <td class="numeric-cell amount-cell dashed-total">{{ money(subtotal) }}</td>
              </tr>
              <tr v-if="page.isLast && discountPct" class="totals-row discount-row">
                <td></td>
                <td></td>
                <td class="total-label">Discount %</td>
                <td></td>
                <td class="numeric-cell amount-cell">{{ formatPercent(discountPct) }}%</td>
              </tr>
              <tr v-if="page.isLast" class="totals-row grand-total-row">
                <td></td>
                <td></td>
                <td class="total-label">Total Amount</td>
                <td class="currency-code">USD</td>
                <td class="numeric-cell amount-cell dashed-total">${{ money(total) }}</td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>

      <footer class="print-footer">
        <div class="disclaimer">
          The quantities listed are intended for bidding purposes only, as a service to our clients. Western Interlock Rickreall does not guarantee the accuracy of the materials listed herein.
          The customer has full responsibility to do a complete take-off to determine the suitability of their quotation to the job drawings and specifications. This is a firm quotation for the
          materials listed herein only. Prices subject to change without notice.
        </div>
        <div class="brand-footer">Basalite Building Products, LLC</div>
      </footer>
    </section>
  </div>
</template>

<script>
import logoPrint from '@/assets/logo-print.png';
import { calculateLineAmount, calculateQuoteTotalWeight, formatCurrency } from '@/composables/useQuoteLinePricing';
import { isLineStarted } from '@/composables/useQuoteLineItems';
import { getPhoneNumber } from '@/composables/usePhoneTypes';
import useQuoteProductsQuery from '@/composables/useQuoteProductsQuery';

const FIRST_PAGE_DETAILS_HEIGHT = 596;
const CONTINUATION_PAGE_DETAILS_HEIGHT = 875;
const DETAILS_HEADING_HEIGHT = 25;
const TABLE_HEADER_HEIGHT = 25;
const TOTALS_HEIGHT = 104;
const MIN_ROW_HEIGHT = 34;

export default {
  name: 'QuotePrintDocument',
  props: {
    quote: {
      type: Object,
      required: true,
    },
    selectedCompany: {
      type: Object,
      default: null,
    },
    selectedIndividual: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      logoPrint,
      pages: [],
      paginateTimer: null,
      displayNotes: '',
    };
  },
  computed: {
    company() {
      return this.selectedCompany || this.quote.company || {};
    },
    individual() {
      return this.selectedIndividual || this.quote.contact || {};
    },
    isIndividualCustomer() {
      return !this.quote.COMPANYID && !!this.quote.CONTACTID;
    },
    customer() {
      return this.isIndividualCustomer ? this.individual : this.company;
    },
    customerName() {
      if (!this.isIndividualCustomer) return this.company.NAME || '';
      return `${this.individual.FIRSTNAME || ''} ${this.individual.LASTNAME || ''}`.trim();
    },
    quoteNumber() {
      return this.quote.QUOTENUM ? `#${this.quote.QUOTENUM}` : '';
    },
    printableLines() {
      return (this.quote.items || [])
        .filter((line) => isLineStarted(line))
        .map((line, index) => ({
          ...line,
          printKey: line.ITEMID || line.CLIENTID || `${index}-${line.SORTORDER || 0}`,
          printNumber: line.LINETYPE === 'comment' ? '' : (index + 1) * 10,
          isComment: line.LINETYPE === 'comment',
          amount: calculateLineAmount(line),
        }));
    },
    subtotal() {
      return this.printableLines.reduce((sum, line) => sum + line.amount, 0);
    },
    discountPct() {
      return Number(this.quote.DISCOUNTPCT || 0);
    },
    discountAmount() {
      return this.subtotal * (this.discountPct / 100);
    },
    total() {
      return this.subtotal - this.discountAmount;
    },
    customerAddressLines() {
      const address = this.customer.address || {};
      return [
        this.customerName,
        address.LINE1,
        address.LINE2,
        this.cityStateZip(address),
      ]
        .filter(Boolean)
        .map((line) => String(line).toUpperCase());
    },
    customerPhone() {
      if (this.isIndividualCustomer) {
        return getPhoneNumber(this.individual.phones, 'direct');
      }
      return getPhoneNumber(this.company.phones, 'main') || getPhoneNumber(this.company.phones, 'tollFree');
    },
    shippingAddressLines() {
      return [
        this.quote.SHIPLINE1,
        this.quote.SHIPLINE2,
        this.cityStateZip({
          CITY: this.quote.SHIPCITY,
          STATE: this.quote.SHIPSTATE,
          POSTALCODE: this.quote.SHIPPOSTALCODE,
        }),
      ]
        .filter(Boolean)
        .map((line) => String(line).toUpperCase());
    },
    totalWeight() {
      return calculateQuoteTotalWeight(this.quote, useQuoteProductsQuery.quoteProducts);
    },
    formattedTotalWeight() {
      return `${formatCurrency(this.totalWeight)} lbs`;
    },
  },
  watch: {
    quote: {
      handler() {
        this.schedulePaginate();
      },
      deep: true,
    },
    selectedCompany: {
      handler() {
        this.schedulePaginate();
      },
      deep: true,
    },
    selectedIndividual: {
      handler() {
        this.schedulePaginate();
      },
      deep: true,
    },
  },
  mounted() {
    this.schedulePaginate();
  },
  beforeUnmount() {
    if (this.paginateTimer) window.cancelAnimationFrame(this.paginateTimer);
  },
  methods: {
    async prepareForPrint() {
      await this.waitForAssets();
      await this.waitForFonts();
      await this.$nextTick();
      await this.waitForMeasureRows();
      this.paginate();
      await this.$nextTick();
      this.truncateDisplayNotes();
      await this.$nextTick();
      return this.pages.length > 0;
    },
    async waitForMeasureRows() {
      const expectedRows = this.printableLines.length;
      if (!expectedRows) return;

      for (let attempt = 0; attempt < 20; attempt += 1) {
        const rows = this.$refs.measureRows;
        const rowArray = Array.isArray(rows) ? rows : rows ? [rows] : [];
        if (rowArray.length >= expectedRows) return;
        await this.$nextTick();
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }
    },
    schedulePaginate() {
      if (this.paginateTimer) window.cancelAnimationFrame(this.paginateTimer);
      this.paginateTimer = window.requestAnimationFrame(async () => {
        this.paginateTimer = null;
        this.paginate();
        await this.$nextTick();
        this.truncateDisplayNotes();
      });
    },
    paginate() {
      const lineHeights = this.getMeasuredLineHeights();
      const lines = this.printableLines.map((line, index) => ({
        ...line,
        measuredHeight: lineHeights[index] || MIN_ROW_HEIGHT,
      }));

      if (!lines.length) {
        const detailsHeight = FIRST_PAGE_DETAILS_HEIGHT;
        const fillerHeight = this.bodyCapacity(detailsHeight, true);
        this.pages = [this.makePage(0, true, true, [], detailsHeight, fillerHeight)];
        return;
      }

      const pages = [];
      let lineIndex = 0;

      while (lineIndex < lines.length) {
        const isFirst = pages.length === 0;
        const detailsHeight = isFirst ? FIRST_PAGE_DETAILS_HEIGHT : CONTINUATION_PAGE_DETAILS_HEIGHT;
        const pageRows = [];
        let usedHeight = 0;

        while (lineIndex < lines.length) {
          const line = lines[lineIndex];
          const isLastCandidate = lineIndex === lines.length - 1;
          const capacity = this.bodyCapacity(detailsHeight, isLastCandidate);

          if (pageRows.length && usedHeight + line.measuredHeight > capacity) break;

          pageRows.push(line);
          usedHeight += line.measuredHeight;
          lineIndex += 1;

          if (isLastCandidate && usedHeight > this.bodyCapacity(detailsHeight, true) && pageRows.length > 1) {
            const moved = pageRows.pop();
            usedHeight -= moved.measuredHeight;
            lineIndex -= 1;
          }
        }

        const isLast = lineIndex >= lines.length;
        const fillerHeight = Math.max(0, this.bodyCapacity(detailsHeight, isLast) - usedHeight);
        pages.push(this.makePage(pages.length, isFirst, isLast, pageRows, detailsHeight, fillerHeight));
      }

      this.pages = pages;
    },
    makePage(index, isFirst, isLast, rows, detailsHeight, fillerHeight) {
      return {
        key: `quote-print-page-${index}`,
        isFirst,
        isLast,
        rows,
        detailsHeight,
        fillerHeight,
      };
    },
    bodyCapacity(detailsHeight, includesTotals) {
      const totalsHeight = includesTotals ? TOTALS_HEIGHT : 0;
      return detailsHeight - DETAILS_HEADING_HEIGHT - TABLE_HEADER_HEIGHT - totalsHeight;
    },
    getMeasuredLineHeights() {
      const rows = this.$refs.measureRows;
      const rowArray = Array.isArray(rows) ? rows : rows ? [rows] : [];
      return rowArray.map((row) => Math.max(MIN_ROW_HEIGHT, Math.ceil(row.getBoundingClientRect().height)));
    },
    waitForAssets() {
      return new Promise((resolve) => {
        const image = new Image();
        image.onload = resolve;
        image.onerror = resolve;
        image.src = logoPrint;
        if (image.complete) resolve();
      });
    },
    waitForFonts() {
      if (!document.fonts?.ready) return Promise.resolve();
      return document.fonts.ready.catch(() => {});
    },
    rowClass(line) {
      return {
        'quote-line-row': true,
        'comment-line-row': line.isComment,
      };
    },
    descriptionParts(line) {
      const description = String(line.DESCRIPTION || '').trim();
      if (!description) return { code: '', details: [] };

      const separatorIndex = description.indexOf(' - ');
      if (separatorIndex === -1) return { code: '', details: [description] };
      const code = description.slice(0, separatorIndex);
      const rest = description.slice(separatorIndex + 3);

      return {
        code,
        details: rest
          .split(' / ')
          .map((part) => part.trim())
          .filter(Boolean),
      };
    },
    cityStateZip(address = {}) {
      const city = address.CITY || '';
      const state = address.STATE || '';
      const postalCode = address.POSTALCODE || '';
      const cityState = [city, state].filter(Boolean).join(' ');
      return [cityState, postalCode].filter(Boolean).join(' ');
    },
    formatQuantity(line) {
      const quantity = Number(line.QTY || 0);
      return quantity.toLocaleString('en-US', { maximumFractionDigits: 3 });
    },
    lineDiscountPct(line) {
      return Number(line.DISCOUNTPCT || 0);
    },
    unitAbbreviation(unit) {
      const normalized = String(unit || 'EA')
        .trim()
        .toUpperCase();
      return ['EA', 'BND', 'FT2'].includes(normalized) ? normalized : 'EA';
    },
    money(value) {
      return formatCurrency(value);
    },
    formatPercent(value) {
      const number = Number(value || 0);
      return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
    },
    formatDate(value) {
      const date = new Date(value);
      if (date.toString() === 'Invalid Date') return value;
      return date.toLocaleDateString('en-US');
    },
    truncateDisplayNotes() {
      const notes = String(this.quote.NOTES || '').trim();
      if (!notes) {
        this.displayNotes = '';
        return;
      }

      const panel = this.refElement(this.$refs.branchPanelBody);
      const measure = this.refElement(this.$refs.notesMeasure);
      if (!panel || !measure) {
        this.displayNotes = notes;
        return;
      }

      const fixedEl = this.refElement(this.$refs.branchFixedLines);
      const panelHeight = panel.clientHeight;
      const fixedHeight = fixedEl?.getBoundingClientRect().height || 0;
      const notesMargin = 8;
      const availableHeight = panelHeight - fixedHeight - notesMargin;

      if (availableHeight <= 0) {
        this.displayNotes = '...';
        return;
      }

      const prefix = 'Notes: ';
      const fits = (text) => {
        measure.textContent = `${prefix}${text}`;
        return measure.getBoundingClientRect().height <= availableHeight;
      };

      if (fits(notes)) {
        this.displayNotes = notes;
        return;
      }

      let low = 0;
      let high = notes.length;
      let best = 0;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const candidate = mid >= notes.length ? notes : `${notes.slice(0, mid)}...`;
        if (fits(candidate)) {
          best = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      if (best >= notes.length) {
        this.displayNotes = notes;
      } else if (best <= 0) {
        this.displayNotes = '...';
      } else {
        this.displayNotes = `${notes.slice(0, best)}...`;
      }
    },
    refElement(ref) {
      if (Array.isArray(ref)) return ref[0] || null;
      return ref || null;
    },
  },
};
</script>

<style lang="scss" scoped>
.quote-print-stage {
  position: fixed;
  top: 0;
  left: -10000px;
  width: 8.5in;
  color: #111;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 12px;
  line-height: 1.18;
  pointer-events: none;
  visibility: hidden;
}

.quote-print-stage,
.quote-print-stage * {
  font-family: Arial, Helvetica, sans-serif;
}

.quote-print-measure {
  position: absolute;
  top: 0;
  left: 0;
  width: calc(8.5in - 0.9in);
  visibility: hidden;
}

.quote-print-page {
  page: quotePrintDocument;
  width: 8.5in;
  height: 11in;
  box-sizing: border-box;
  padding: 0.34in 0.45in 0.22in;
  background: white;
  display: grid;
  grid-template-rows: 0.72in 1fr 0.48in;
  row-gap: 0;
  page-break-after: always;
  break-after: page;
  overflow: hidden;
}

.quote-print-page:last-child {
  page-break-after: auto;
  break-after: auto;
}

.print-header {
  display: grid;
  grid-template-columns: 1.8in 2.4in 1fr;
  align-items: start;
}

.logo-block img {
  display: block;
  width: 1.56in;
  height: auto;
}

.letterhead {
  padding-top: 0.02in;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.15;
}

.letterhead strong {
  display: inline-block;
  min-width: 0.42in;
}

.quotation-title {
  justify-self: end;
  text-align: right;
  font-size: 27px;
  font-weight: 700;
  line-height: 1.05;
}

.quotation-title span {
  display: block;
  margin-top: 0.04in;
}

.print-body {
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-top: 0.13in;
  gap: 0.18in;
}

.first-page-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1.33in 1.33in;
  gap: 0.06in 0.16in;
}

.info-panel {
  border: 1px solid #111;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.branch-panel {
  grid-column: 2;
  grid-row: 1 / span 2;
}

.info-panel h2,
.details-panel h2 {
  margin: 0;
  padding: 4px 6px 3px;
  background: #d9d9d9;
  border-bottom: 1px solid #111;
  font-size: 16px;
  line-height: 1.1;
}

.details-panel h2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-number {
  font-size: 10px;
  font-weight: 700;
}

.panel-body {
  padding: 9px 17px;
  font-size: 13px;
  font-weight: 700;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.branch-panel-body {
  display: flex;
  flex-direction: column;
}

.branch-fixed-lines {
  flex-shrink: 0;
}

.notes-line {
  margin-top: 8px;
  flex: 1;
  min-height: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.notes-measure {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  width: calc((8.5in - 0.9in - 0.16in) / 2 - 34px);
  padding: 0;
  margin: 0;
  flex: none;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.18;
}

.phone-line {
  margin-top: 8px;
}

.valid-to-line,
.total-weight-line {
  margin-top: 8px;
}

.details-panel {
  border: 1px solid #111;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.details-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 11px;
  line-height: 1.22;
}

.details-panel .details-table {
  flex: 1;
}

.item-col {
  width: 0.48in;
}

.description-col {
  width: auto;
}

.qty-col {
  width: 1.08in;
}

.price-col {
  width: 1.04in;
}

.amount-col {
  width: 1.1in;
}

.details-table th {
  border-bottom: 1px solid #111;
  border-right: 1px solid #111;
  padding: 4px 6px;
  font-size: 13px;
  line-height: 1.05;
  text-align: left;
}

.details-table th:nth-child(n + 3) {
  text-align: center;
}

.details-table th:last-child,
.details-table td:last-child {
  border-right: none;
}

.details-table td {
  border-right: 1px solid #111;
  padding: 9px 7px;
  vertical-align: top;
}

.quote-line-row {
  break-inside: avoid;
  page-break-inside: avoid;
}

.item-number {
  font-weight: 700;
  text-align: left;
}

.description-cell {
  font-weight: 700;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.material-code {
  margin-bottom: 2px;
}

.comment-text {
  font-style: italic;
  font-weight: 700;
}

.numeric-cell {
  text-align: right;
  font-weight: 700;
}

.line-discount {
  margin-top: 4px;
}

.filler-row td {
  padding: 0;
}

.totals-row td {
  padding: 6px 7px;
  vertical-align: middle;
}

.total-label,
.currency-code {
  font-size: 12px;
  font-weight: 700;
}

.currency-code {
  text-align: center;
}

.dashed-total {
  border-top: 1px dashed #111;
  border-bottom: 1px dashed #111;
}

.grand-total-row .dashed-total {
  border-bottom: none;
}

.grand-total-row td {
  padding-top: 9px;
  padding-bottom: 9px;
}

.grand-total-row .total-label,
.grand-total-row .currency-code,
.grand-total-row .amount-cell {
  font-size: 14px;
}

.grand-total-row .amount-cell {
  white-space: nowrap;
}

.print-footer {
  display: grid;
  grid-template-columns: 3.92in 1fr;
  column-gap: 0.28in;
  align-items: stretch;
}

.disclaimer {
  align-self: stretch;
  border: 1px solid #111;
  border-top: none;
  padding: 4px 5px;
  font-size: 6.5px;
  line-height: 1.12;
  font-weight: 700;
}

.brand-footer {
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 0;
  font-size: 20px;
  line-height: 1.1;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
}

@media print {
  .quote-print-stage {
    position: static;
    left: auto;
    width: auto;
    pointer-events: auto;
    visibility: visible;
  }

  .quote-print-measure {
    display: none;
  }

  .quote-print-page {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>

<style lang="scss">
@media print {
  @page quotePrintDocument {
    size: 8.5in 11in;
    margin: 0;
  }
}
</style>
